import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Grid, Typography,
  Accordion, AccordionSummary, AccordionDetails, Box
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { VendorSelectionDialogProps } from './types';
import { useSwipeable } from 'react-swipeable';

export const VendorSelectionDialog: React.FC<VendorSelectionDialogProps> = ({
  open,
  onClose,
  vendors,
  vendorMembers,
  currentMembers,
  onVendorMemberSelect,
  expandedVendors,
  onVendorExpand
}) => {
  // 選択中のメンバーID
  const selectedVendorMemberIds = currentMembers
    .filter(m => m.VendorMember_ID)
    .map(m => m.VendorMember_ID as number);

  // アニメーション効果用の状態
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  
  // コンポーネントがマウントされたかどうかを追跡
  const [mounted, setMounted] = useState(false);
  
  // 現在の業者インデックス
  const currentVendorIndex = expandedVendors.length > 0
    ? vendors.findIndex(v => v.ID === expandedVendors[0])
    : -1;
    
  // ダイアログの内容をログ出力（デバッグ用）
  useEffect(() => {
    if (open) {
      console.log('Dialog opened', {
        vendors: vendors.length,
        vendorMembers: vendorMembers.length,
        expandedVendors
      });
      setMounted(true);
    }
  }, [open, vendors, vendorMembers, expandedVendors]);
    
  // スワイプハンドラー設定
  const swipeHandlers = useSwipeable({
    onSwiping: (eventData) => {
      console.log('Swiping:', {
        dir: eventData.dir, 
        deltaY: eventData.deltaY,
        deltaX: eventData.deltaX
      });
    },
    onSwipedUp: () => {
      console.log('Swiped UP');
      // 次の業者に移動
      if (vendors.length <= 1) return;
      
      const nextIndex = Math.min(vendors.length - 1, (currentVendorIndex !== -1 ? currentVendorIndex : 0) + 1);
      if (nextIndex !== currentVendorIndex) {
        // 現在のアコーディオンをすべて閉じる
        if (expandedVendors.length > 0) {
          expandedVendors.forEach(id => onVendorExpand(id, false));
        }
        
        // 次のアコーディオンを開く
        const nextVendorId = vendors[nextIndex]?.ID;
        if (nextVendorId) {
          onVendorExpand(nextVendorId, true);
          setActiveIndex(nextIndex);
          setTimeout(() => setActiveIndex(null), 300);
        }
      }
    },
    onSwipedDown: () => {
      console.log('Swiped DOWN');
      // 前の業者に移動
      if (vendors.length <= 1 || currentVendorIndex <= 0) return;
      
      const prevIndex = Math.max(0, currentVendorIndex - 1);
      if (prevIndex !== currentVendorIndex) {
        // 現在のアコーディオンをすべて閉じる
        if (expandedVendors.length > 0) {
          expandedVendors.forEach(id => onVendorExpand(id, false));
        }
        
        // 前のアコーディオンを開く
        const prevVendorId = vendors[prevIndex]?.ID;
        if (prevVendorId) {
          onVendorExpand(prevVendorId, true);
          setActiveIndex(prevIndex);
          setTimeout(() => setActiveIndex(null), 300);
        }
      }
    },
    preventScrollOnSwipe: true,
    trackTouch: true,
    trackMouse: false,
    delta: 10
  });

  // ダイアログが開いたときに初期設定
  useEffect(() => {
    if (open && vendors.length > 0 && expandedVendors.length === 0) {
      // 最初の業者のアコーディオンを開く
      onVendorExpand(vendors[0].ID, true);
    }
  }, [open, vendors, expandedVendors, onVendorExpand]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle sx={{
        borderBottom: 1,
        borderColor: 'divider',
        bgcolor: 'grey.50',
        px: 3,
        py: 2
      }}>
        外注メンバー選択 {vendors?.length || 0}社
      </DialogTitle>

      {/* スワイプガイド */}
      {vendors.length > 1 && (
        <Box sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          py: 1,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
          color: 'text.secondary'
        }}>
          <Typography variant="caption">
            ↑↓ スワイプで業者切り替え
          </Typography>
        </Box>
      )}

      <DialogContent sx={{ p: 2 }}>
        {/* スワイプハンドラーはこのBoxに適用 */}
        <div
          {...swipeHandlers}
          style={{
            width: '100%',
            minHeight: '100px',
            touchAction: 'none',
            WebkitUserSelect: 'none',
            userSelect: 'none',
            border: '1px solid #ddd',
            boxSizing: 'border-box'
          }}
        >
          <div 
            style={{
              background: 'lightblue',
              padding: '20px',
              margin: '10px 0',
              textAlign: 'center',
              fontSize: '16px',
              fontWeight: 'bold'
            }}
          >
            スワイプテスト - ここを上下にスワイプ
          </div>
         
          {/* 業者データの有無を確認 */}
          {vendors.length === 0 ? (
            <Typography sx={{ p: 2, textAlign: 'center' }}>
              業者データがありません
            </Typography>
          ) : (
            vendors.map((vendor, index) => (
              <Accordion
                key={vendor.ID}
                expanded={expandedVendors.includes(vendor.ID)}
                onChange={(_, isExpanded) => onVendorExpand(vendor.ID, isExpanded)}
                sx={{
                  mb: 1,
                  '&.Mui-expanded': {
                    backgroundColor: 'action.hover',
                    mb: 2
                  },
                  transition: 'all 0.3s ease',
                  ...(activeIndex === index && {
                    boxShadow: 3,
                    transform: 'scale(1.01)',
                  })
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    '&.Mui-expanded': {
                      backgroundColor: 'action.hover',
                      borderBottom: 1,
                      borderColor: 'divider'
                    }
                  }}
                >
                  <Typography sx={{ fontWeight: 'medium' }}>
                    {vendor.VendorName || `業者 ${index + 1}`}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 2 }}>
                  <Grid container spacing={1.5}>
                    {vendorMembers
                      .filter(m => m.VendorID === vendor.ID)
                      .map(member => {
                        const isSelected = selectedVendorMemberIds.includes(member.ID);

                        return (
                          <Grid item xs={4} key={`vendor-${member.ID}`}>
                            <Button
                              fullWidth
                              variant={isSelected ? "contained" : "outlined"}
                              color={isSelected ? "success" : "primary"}
                              onClick={() => onVendorMemberSelect(member, vendor.VendorName)}
                              sx={{
                                height: '60px',
                                whiteSpace: 'normal',
                                lineHeight: 1.2,
                                textTransform: 'none',
                                ...(isSelected && {
                                  fontWeight: 'bold',
                                  boxShadow: 1
                                })
                              }}
                            >
                              <Typography
                                variant="body2"
                                align="center"
                              >
                                {member.MemberName || `メンバー ${member.ID}`}
                              </Typography>
                            </Button>
                          </Grid>
                        );
                      })}
                  </Grid>
                </AccordionDetails>
              </Accordion>
            ))
          )}
        </div>
      </DialogContent>
      
      <DialogActions sx={{
        borderTop: 1,
        borderColor: 'divider',
        px: 3,
        py: 2
      }}>
        <Button
          onClick={onClose}
          variant="outlined"
        >
          閉じる
        </Button>
      </DialogActions>
    </Dialog>
  );
};